import React, { Dispatch, SetStateAction, useMemo, useState, memo } from 'react';
import { Box, Button, Card, useTheme } from '@mui/material';
import { Autoclave, AutoclaveType, Frequency, OpenAutoclaveModal, OpenAssignBoxModal, SporetestingResult, OpenSporetestModal, SporetestLog } from '../types';
import { Biobox, ComplianceStatus, TeamMember } from '../../../../types';
import ComplianceMessage from './ComplianceMessage';
import DownloadSporetestModal from './DownloadSporetestModal';
import EnvelopeInstructionsModal from './EnvelopeInstructionsModal';
import AutoclaveStatusModal from './AutoclaveStatusModal';
import AutoclaveDeleteModal from './AutoclaveDeleteModal';
import AutoclaveWarningModal from './AutoclaveWarningModal';
import AddressSporeTestModal from './AddressSporetestModal';
import ReassignSporetestModal from './ReassignSporetestModal';
import AssignedBioboxes from './AssignedBioboxes';
import AutoclaveTable from './AutoclaveTable';
import AutoclaveMenu from './AutoclaveMenu';
import AutoclaveHeader from './AutoclaveHeader';
import useCustomerNumber from '../../../../hooks/useCustomerNumber';
import { AutoclaveSelection, AutoclaveTypeId } from '../enums';
import AutoclaveToggle from './autoclave/AutoclaveToggle';
import SporetestLogTable from './autoclave/SporetestLogTable';
import { CustomerState } from '../../../../redux/customer';
import { ChildCustomerState } from '../../../../redux/childCustomer';

interface AutoclaveProps {
  autoclave: Autoclave;
  autoclaves: Autoclave[];
  customer: CustomerState | ChildCustomerState;
  setAutoclaves: Dispatch<SetStateAction<Autoclave[]>>;
  setSporetests: Dispatch<SetStateAction<SporetestingResult[]>>;
  setOpenAssignBioboxModal: Dispatch<SetStateAction<OpenAssignBoxModal>>;
  setOpenAutoclaveModal: Dispatch<SetStateAction<OpenAutoclaveModal>>;
  setOpenSporetestModal: Dispatch<SetStateAction<OpenSporetestModal>>;
  autoclaveTypeById: Map<AutoclaveTypeId, AutoclaveType>;
  autoclaveFrequencyById: Map<number, Frequency>;
  bioboxesById: Map<number, Biobox>;
  sporetestsById: Map<number, SporetestingResult>;
  sporetestsByAutoclaveId: Map<number, SporetestingResult[]>;
  sporetestLogsByAutoclaveId: Map<number, SporetestLog[]>;
  usersById: Map<number, TeamMember>;
}

const AutoclaveItem: React.FC<AutoclaveProps> = (props) => {
  const {
    autoclave,
    autoclaves,
    customer,
    setAutoclaves,
    setSporetests,
    setOpenAssignBioboxModal,
    setOpenAutoclaveModal,
    setOpenSporetestModal,
    autoclaveTypeById,
    autoclaveFrequencyById,
    bioboxesById,
    sporetestsById,
    sporetestsByAutoclaveId,
    sporetestLogsByAutoclaveId,
    usersById,
  } = props

  const theme = useTheme()
  const customerNumber = useCustomerNumber()

  const [openDownloadModal, setOpenDownloadModal] = useState<boolean>(false)
  const [openEnvelopeInstructionsModal, setOpenEnvelopeInstructionsModal] = useState<boolean>(false)
  const [openAutoclaveStatusModal, setOpenAutoclaveStatusModal] = useState<boolean>(false)
  const [openDeleteAutoclaveModal, setOpenDeleteAutoclaveModal] = useState<boolean>(false)
  const [openMarkAsAddressed, setOpenMarkAsAddressed] = useState<boolean>(false)
  const [openReassignSporetest, setOpenReassignSporetest] = useState<boolean>(false)
  const [selectedSporeTest, setSelectedSporeTest] = useState<SporetestingResult | null>(null);
  const [selection, setSelection] = useState<AutoclaveSelection>(AutoclaveSelection.TestResults)

  const bioboxes = Array.from(bioboxesById.values())
  const autoclaveTestResults = sporetestsByAutoclaveId.get(Number(autoclave.autoclave_id)) ?? []

  const assignedBioboxes = useMemo(() => {
    return bioboxes.filter(box => {
      const { active, box_type, autoclave_id } = box
      return active && box_type === 'mesa' && Number(autoclave_id) === Number(autoclave.autoclave_id)
    });
  }, [autoclave, bioboxes])

  const activeBioboxes = useMemo(() => {
    return assignedBioboxes.filter(box => {
      const { active, tests_run, box_size } = box
      return active && tests_run < box_size
    })
  }, [assignedBioboxes])

  return (
    <Card sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', p: 2, mb: 2, justifyContent: 'space-between' }}>
        <AutoclaveHeader
          autoclave={autoclave}
          autoclaveTypeById={autoclaveTypeById}
          autoclaveFrequencyById={autoclaveFrequencyById}
          />
        {autoclave.autoclave_compliance.compliance.compliance_status !== ComplianceStatus.Complete && (
          <Box>
            <ComplianceMessage
              status={autoclave.autoclave_compliance.compliance.compliance_status}
              message={autoclave.autoclave_compliance.compliance.message_long}
            />
          </Box>
        )}
        <Box>
          <Box sx={{ display: 'flex', minWidth: '315px', justifyContent: 'flex-end' }}>
            <AutoclaveMenu
              autoclave={autoclave}
              bioboxes={bioboxes}
              sporetestsByAutoclaveId={sporetestsByAutoclaveId}
              setOpenSporetestModal={setOpenSporetestModal}
              setOpenAutoclaveModal={setOpenAutoclaveModal}
              setOpenAutoclaveStatusModal={setOpenAutoclaveStatusModal}
              setOpenDeleteAutoclaveModal={setOpenDeleteAutoclaveModal}
              setOpenEnvelopeInstructionsModal={setOpenEnvelopeInstructionsModal}
            />
            {autoclaveTestResults.length > 0 && (
              <Box sx={{ ml: 1.5 }}>
                <Button onClick={() => setOpenDownloadModal(true)} variant="contained">
                  Export Spore Tests
                </Button>
              </Box>
            )}
          </Box>
          <Box className="m-t-16" sx={{ display: 'flex', minWidth: '315px', justifyContent: 'flex-end' }}>
            <AutoclaveToggle selection={selection} setSelection={setSelection} />
          </Box>
        </Box>
      </Box>
      {
        Boolean(assignedBioboxes.length) && (
          <AssignedBioboxes
            autoclave={autoclave}
            bioboxes={assignedBioboxes}
            setOpenAssignBioboxModal={setOpenAssignBioboxModal}
          />
        )
      }
      {
        autoclaveTestResults.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', color: theme.palette.color50555b.main, fontSize: '13px', mb: 2 }}>
            There are no spore tests recorded yet for this autoclave.
          </Box>
        )
      }
      {
        selection === AutoclaveSelection.TestResults && Boolean(autoclaveTestResults.length) && (
          <AutoclaveTable
            autoclave={autoclave}
            sporetestsByAutoclaveId={sporetestsByAutoclaveId}
            setOpenMarkAsAddressed={setOpenMarkAsAddressed}
            setOpenReassignSporetest={setOpenReassignSporetest}
            setOpenSporetestModal={setOpenSporetestModal}
            setSelectedSporeTest={setSelectedSporeTest}
          />
        )
      }
      {
        selection === AutoclaveSelection.TestLogs && (
          <SporetestLogTable
            autoclave={autoclave}
            bioboxesById={bioboxesById}
            sporetestsById={sporetestsById}
            usersById={usersById}
            sporetestLogsByAutoclaveId={sporetestLogsByAutoclaveId}
          />
        )
      }
      {openDownloadModal && (
        <DownloadSporetestModal
          autoclaveId={autoclave.autoclave_id}
          autoclaves={[autoclave]}
          autoclaveTypeById={autoclaveTypeById}
          autoclaveFrequencyById={autoclaveFrequencyById}
          sporetestsByAutoclaveId={sporetestsByAutoclaveId}
          customer={customer}
          customerNumber={customerNumber}
          open={openDownloadModal}
          handleClose={() => setOpenDownloadModal(false)}
          unassignedTestResults={[]}
        />
      )}
      {openEnvelopeInstructionsModal && (
        <EnvelopeInstructionsModal
          autoclave={autoclave}
          autoclaveTypeById={autoclaveTypeById}
          open={openEnvelopeInstructionsModal}
          handleClose={() => setOpenEnvelopeInstructionsModal(false)}
        />
      )}
      {openAutoclaveStatusModal && (
        <AutoclaveStatusModal
          open={openAutoclaveStatusModal}
          autoclave={autoclave}
          customerNumber={customerNumber}
          setAutoclaves={setAutoclaves}
          handleClose={() => setOpenAutoclaveStatusModal(false)}
        />
      )}
      {openDeleteAutoclaveModal && !Boolean(activeBioboxes.length) && (
        <AutoclaveDeleteModal
          open={openDeleteAutoclaveModal}
          autoclave={autoclave}
          customerNumber={customerNumber}
          setAutoclaves={setAutoclaves}
          handleClose={() => setOpenDeleteAutoclaveModal(false)}
        />
      )}
      {openDeleteAutoclaveModal && Boolean(activeBioboxes.length) && (
        <AutoclaveWarningModal
          open={openDeleteAutoclaveModal}
          handleClose={() => setOpenDeleteAutoclaveModal(false)}
        />
      )}
      {openMarkAsAddressed && selectedSporeTest && (
        <AddressSporeTestModal
          customerNumber={customerNumber}
          open={openMarkAsAddressed}
          sporetest={selectedSporeTest}
          handleClose={() => setOpenMarkAsAddressed(false)}
          setAutoclaves={setAutoclaves}
          setSporetests={setSporetests}
        />
      )}
      {openReassignSporetest && selectedSporeTest && (
        <ReassignSporetestModal
          open={openReassignSporetest}
          sporetest={selectedSporeTest}
          autoclave={autoclave}
          autoclaves={autoclaves}
          setAutoclaves={setAutoclaves}
          setSporetests={setSporetests}
          handleClose={() => setOpenReassignSporetest(false)}
          customerNumber={customerNumber}
        />
      )}
    </Card>
  );
};

export default memo(AutoclaveItem)
